import {
   CurrentPractitionerUpdateEwusPasswordCommand,
   EwusAccountBlockedFault,
   EwusAccountLoginFault,
   EwusPasswordChangeFault,
   EwusPasswordChangeUnknownFault,
} from '@dreryk/egabinet-ewus-frontend-api';

import { useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { dispatchSnackbarNegativeEvent, dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { Choice, Slot } from '@dreryk/watermelon';
import { createSingleInterpolatedTranslationKey } from '@dreryk/react-common';

import { nfzLoginTranslationKeys } from '../../../translations';

import { NfzLoginFormBean } from './NfzLoginForm.types';
import { PasswordChangeEnabledFormBeanPart } from './fields/PasswordChangeEnabledField';
import { mfaStrategyVisitor } from './NfzLoginForm.utils';
import { EwusEnabledFormBeanPart } from './fields/EwusEnabledField';

type CurrentPractitionerUpdateEwusPasswordCommandOptions = {
   onSuccess: VoidFunction;
};

export const useCurrentPractitionerUpdateEwusPasswordCommand = (
   options: CurrentPractitionerUpdateEwusPasswordCommandOptions
): UseCqrsCommandResult<
   void,
   NfzLoginFormBean<PasswordChangeEnabledFormBeanPart<true> & EwusEnabledFormBeanPart<true>>,
   EwusAccountLoginFault | EwusPasswordChangeFault | EwusPasswordChangeUnknownFault | EwusAccountBlockedFault
> => {
   return useCqrsCommand(
      (parameters) => {
         const command = new CurrentPractitionerUpdateEwusPasswordCommand();
         command.personnelCode = Slot.emptyIfNull(parameters.personnelCode);
         command.login = parameters.login!;
         command.oldPassword = parameters.password!;
         command.newPassword = parameters.newPassword!;

         mfaStrategyVisitor(parameters)({
            visitSecretCode: ({ mfaSecret }) => {
               command.mfaSecret = Choice.withUseDefaultIfNull(mfaSecret);
            },
            visitTotp: ({ mfaAuthorizationCode }) => {
               command.mfaTotp = Choice.of(mfaAuthorizationCode);
            },
         });

         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(nfzLoginTranslationKeys.correctlyChangedPasswordInEwusApplication);
            options.onSuccess();
         },
         onFault: {
            'egabinet-ewus-frontend-api:EwusAccountLoginFault': () =>
               dispatchSnackbarNegativeEvent(nfzLoginTranslationKeys.incorrectCurrentPasswordInEwus),
            'egabinet-ewus-frontend-api:EwusPasswordChangeFault': (fault) =>
               dispatchSnackbarNegativeEvent(
                  createSingleInterpolatedTranslationKey(
                     nfzLoginTranslationKeys.messageFromEWUS,
                     'message',
                     fault.result
                  )
               ),
            'egabinet-ewus-frontend-api:EwusPasswordChangeUnknownFault': () =>
               dispatchSnackbarNegativeEvent(
                  nfzLoginTranslationKeys.unknownErrorOccurredWhileChangingPasswordInEwusApplication
               ),
            'egabinet-ewus-frontend-api:EwusAccountBlockedFault': () =>
               dispatchSnackbarNegativeEvent(nfzLoginTranslationKeys.nfzAccountBlocked),
         },
      }
   );
};
